/**
 * Input - Base text input atom used across all forms in the application.
 * Handles label rendering, error/helper text display, and accessible markup.
 * Enforces a 44px minimum height for mobile touch target compliance.
 *
 * For form-level usage with consistent spacing, prefer the FormField molecule
 * which wraps this component.
 *
 * @example
 * <Input label="Email" error="Required field" required />
 * <Input placeholder="Search..." helperText="Type to filter results" />
 */
import React from 'react';

/**
 * Props for the Input component.
 * Extends native HTML input attributes so callers can pass type, placeholder, onChange, etc.
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	/** Optional visible label displayed above the input */
	label?: string;
	/** Error message — when present, input shows error styling and the message below */
	error?: string;
	/** Supplementary hint text shown below the input (hidden when error is present) */
	helperText?: string;
}

const Input: React.FC<InputProps> = ({ label, error, helperText, required, id, className = '', ...rest }) => {
	// Auto-generate an id from the label text if none is provided.
	// This ensures the <label> htmlFor always has a matching input id for accessibility.
	const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

	// --- Base input styles ---
	// min-h-44px ensures the input meets WCAG touch target requirements.
	// rounded-6px and px/py values align with the design system spacing scale.
	const baseClasses = 'block w-full rounded-6px border px-12px py-10px text-16px font-sans min-h-44px placeholder-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0';

	// --- Conditional state styling ---
	// Error state uses red borders/ring; normal state uses brand colors.
	const stateClasses = error
		? 'border-red-500 focus:border-red-500 focus:ring-red-500'
		: 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary';

	return (
		<div className="input w-full" data-ref="atom-input">
			{/* Label with optional required indicator */}
			{label && (
				<label htmlFor={inputId} className="input__label block text-14px font-sans font-medium text-gray-700 mb-4px">
					{label}
					{/* Red asterisk signals required field — placed inside label for screen reader association */}
					{required && <span className="text-red-500 ml-2px">*</span>}
				</label>
			)}
			{/* aria-invalid and aria-describedby link the input to its error message for screen readers */}
			<input id={inputId} required={required} className={`input__field${error ? ' input__field--error' : ''} ${baseClasses} ${stateClasses} ${className}`} aria-invalid={error ? 'true' : undefined} aria-describedby={error ? `${inputId}-error` : undefined} {...rest} />
			{/* Error message — linked to input via aria-describedby for assistive technology */}
			{error && (
				<p id={`${inputId}-error`} className="input__error mt-4px text-14px font-sans text-red-600">{error}</p>
			)}
			{/* Helper text only shows when there is no error, to avoid visual clutter */}
			{helperText && !error && (
				<p className="input__helper mt-4px text-14px font-sans text-gray-500">{helperText}</p>
			)}
		</div>
	);
};

export default Input;
